import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";

import { pvOfferingData } from "@/data/pvOfferingData";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getPvOfferingEs, getPvOfferingZh } from "@/i18n/pvOfferings";
import { localizeHref } from "@/lib/localizeHref";

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

/* Map Spanish URL slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "qppv-representante-local": "qppv-local-representative",
  "control-bibliografico": "literature-monitoring",
  "gestion-icsr": "icsr-management",
  "farmacovigilancia-formacion-combinada-certificada": "pharmacovigilance-certified-blended-training",
  "auditorias-fv-inspecciones-qms": "pv-audits-inspections-quality-system-management",
  "informes-seguridad": "aggregate-safety-reporting",
  "inteligencia-regulatoria": "regulatory-intelligence",
  "servicios-integrales-de-informacion-medica": "comprehensive-medical-information-services",
  "servicios-qms-psmf": "qms-psmf-services",
};

const getOfferingPath = (pathname: string): string | null => {
  const match = pathname.match(/\/(?:services\/pharmacovigilance|servicios\/(?:farmacovigilancia|pharmacovigilance))\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  const enSlug = lookupSlug(ES_SLUG_MAP, raw);
  return `/en/services/pharmacovigilance/${enSlug}`;
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  contentTags?: string[];
  faqItems?: { q: string; a: string }[];
}> = {
  "/en/services/pharmacovigilance/literature-monitoring": {
    title: "Literature Monitoring Search Solutions & Alerts",
    subtitle: "Detect safety signals through systematic literature screening",
    description: "Stay compliant with QbD Group's Literature Monitoring Services. Detect safety signals, ensure timely reporting, and enhance your vigilance strategy with systematic searches across scientific publications and databases.",
    contentKey: "literature-monitoring",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/safety-database-solution": {
    title: "Tracking Adverse Drug Reactions",
    subtitle: "Central safety data management for your pharmacovigilance operations",
    description: "Monitor and address adverse drug reactions with precision. QbD Group's safety database solutions support regulatory compliance while prioritizing operational effectiveness and patient care.",
    contentKey: "safety-database-solution",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/vigilance-pharmacovigilance-hotline-services": {
    title: "Vigilance and Pharmacovigilance Hotline Services",
    subtitle: "24/7 support for adverse event reporting and medical inquiries",
    description: "Emergencies don't sleep and neither does our support. The One Vigilance hotline is open 24/7 all year round, providing dedicated pharmacovigilance support for adverse event intake, medical inquiries, and product complaints.",
    contentKey: "vigilance-pharmacovigilance-hotline-services",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/eudravigilance-services": {
    title: "EudraVigilance Medicinal Product Dictionary (XEVMPD)",
    subtitle: "Achieve XEVMPD compliance with expert support",
    description: "Achieve XEVMPD compliance effortlessly with QbD's expertise. Simplify your regulatory submissions and maintain up-to-date safety profiles with comprehensive EudraVigilance data management services.",
    contentKey: "eudravigilance-services",
    contentTags: ["Vigilance", "Regulatory Affairs"],
  },
  "/en/services/pharmacovigilance/project-management": {
    title: "Project Management",
    subtitle: "Coordinate pharmacovigilance projects with structured expertise",
    description: "Coordinate pharmacovigilance projects seamlessly with QbD's project management expertise. Drive safety goals, meet compliance requirements, and improve operational workflows effectively.",
    contentKey: "project-management",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/signal-detection-management": {
    title: "Safety Signal Program",
    subtitle: "Detect risks early with proactive signal management",
    description: "Enhance patient safety with QbD Group's Safety Signal Program. Detect risks early, ensure compliance, and build trust with proactive safety solutions through systematic signal detection and management.",
    contentKey: "signal-detection-management",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/qppv-local-representative": {
    title: "Local QPPV Representative Services",
    subtitle: "Ensure seamless pharmacovigilance compliance across territories",
    description: "Managing pharmacovigilance responsibilities across multiple regions can be complex. At QbD Vigilance, we provide QPPV and Local Representative services tailored to meet the specific regulatory needs of MAHs in Europe, the UK, and beyond.",
    contentKey: "qppv-local-representative",
    contentTags: ["Vigilance", "Regulatory Affairs"],
  },
  "/en/services/pharmacovigilance/icsr-management": {
    title: "ICSRs Management",
    subtitle: "Streamline safety reporting for your clinical trials and marketed products",
    description: "Managing safety case reports effectively is a cornerstone of pharmacovigilance and clinical trial oversight. At QbD Group, our specialized ICSRs Management services ensure timely collection, processing, and reporting of safety information.",
    contentKey: "icsr-management",
    contentTags: ["Vigilance", "Clinical"],
  },
  "/en/services/pharmacovigilance/pharmacovigilance-certified-blended-training": {
    title: "Pharmacovigilance & Certified Blended Training",
    subtitle: "Empower your team with efficient and compliant pharmacovigilance training",
    description: "In the fast-evolving world of pharmacovigilance, ensuring your team is well-trained and compliant with national and international regulations is critical. We offer tailor-made courses and an online training platform designed to elevate compliance standards.",
    contentKey: "pharmacovigilance-certified-blended-training",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/aggregate-safety-reporting": {
    title: "Safety Reporting & Signal Detection",
    subtitle: "Comprehensive safety reporting tailored to your needs",
    description: "Safety reporting is a critical component of pharmacovigilance and post-marketing surveillance. At QbD Group, we analyze and deliver safety reports that ensure compliance with global regulatory requirements.",
    contentKey: "aggregate-safety-reporting",
    contentTags: ["Vigilance"],
  },
  "/en/services/pharmacovigilance/regulatory-intelligence": {
    title: "Regulatory Intelligence",
    subtitle: "Stay ahead with actionable regulatory insights",
    description: "In today's fast-evolving regulatory landscape, staying compliant with international and local legislation is essential. At QbD Group, our Regulatory Intelligence services provide you with the tools and expertise to navigate complex requirements.",
    contentKey: "regulatory-intelligence",
    contentTags: ["Vigilance", "Regulatory Affairs"],
  },
  "/en/services/pharmacovigilance/qms-psmf-services": {
    title: "Pharmacovigilance QMS & PSMF Services",
    subtitle: "Streamline compliance with efficient pharmacovigilance systems",
    description: "Ensuring compliance with national and international pharmacovigilance regulations requires robust systems and meticulous documentation. At QbD Group, we offer a comprehensive Pharmacovigilance QMS and support for your PSMF.",
    contentKey: "qms-psmf-services",
    contentTags: ["Vigilance", "Quality Assurance"],
  },
  "/en/services/pharmacovigilance/pv-audits-inspections-quality-system-management": {
    title: "Pharmacovigilance (PV) Audits",
    subtitle: "Ensure compliance and quality with expert pharmacovigilance audits",
    description: "At QbD Group, we provide certified PV audits, mock inspections, and ISO-related activities to ensure your pharmacovigilance system adheres to Good Pharmacovigilance Practices (GPvP) and international quality standards.",
    contentKey: "pv-audits-inspections-quality-system-management",
    contentTags: ["Vigilance", "Quality Assurance"],
  },
  "/en/services/pharmacovigilance/comprehensive-medical-information-services": {
    title: "Medical Information Services",
    subtitle: "Building trust through expert-driven insight",
    description: "Accurate, timely medical information is more than a regulatory requirement — it is a core pillar of patient safety and product trust. QbD Group combines expert-driven insight with scalable, technology-enabled delivery to ensure your stakeholders always get a clear, compliant answer.",
    contentKey: "comprehensive-medical-information-services",
    contentTags: ["Vigilance"],
  },
};

const PVOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const pharmacovigilancePath = localizeHref("/en/services/pharmacovigilance", lang);

  // Resolve Spanish slugs to English content paths
  const enPath = getOfferingPath(location.pathname) || location.pathname;
  const page = offeringPages[enPath];
  const parentService = getServiceBySlug("pharmacovigilance");

  if (!page || !parentService) return <Navigate to={pharmacovigilancePath} replace />;

  // Get localized overrides if applicable
  const esOverride = lang === "es" ? getPvOfferingEs(page.contentKey) : lang === "zh-cn" ? getPvOfferingZh(page.contentKey) : undefined;

  const displayTitle = esOverride?.title ?? page.title;
  const displaySubtitle = esOverride?.subtitle ?? page.subtitle;
  const displayDescription = esOverride?.description ?? page.description;
  const displayFaqItems = esOverride?.faqItems ?? page.faqItems;

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = `${displayTitle} — ${"Pharmacovigilance"} | QbD Group`;
  const metaDesc = displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Pharmacovigilance"];

  const data = pvOfferingData[page.contentKey];

  return (
    <>
      <SEOHead title={metaTitle} description={metaDesc} canonical={pageUrl} ogImage={pageOgImage}>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "Service", name: displayTitle, description: displayDescription, provider: { "@type": "Organization", name: "QbD Group", url: SITE }, url: pageUrl, isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` }, ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)) })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: displayFaqItems.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: lang === "es" ? "Servicios" : "Services", path: servicesPath },
          { name: "Pharmacovigilance", path: pharmacovigilancePath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar logoOverride={DIVISION_LOGOS["pharmacovigilance"]} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton
          secondaryCta={data?.whatCta}
          parentCrumbs={[
            { label: "Pharmacovigilance", to: pharmacovigilancePath },
          ]}
        />

        {data && (
          <GenericOfferingContent
            data={esOverride ? {
              ...data,
              whatTitle: esOverride.whatTitle,
              whatParagraphs: esOverride.whatParagraphs,
              whyTitle: esOverride.whyTitle,
              whyBenefits: data.whyBenefits.map((b, i) => esOverride.whyBenefits[i] ? { ...b, title: esOverride.whyBenefits[i].title, text: esOverride.whyBenefits[i].text } : b),
              helpCards: esOverride.helpCards ? {
                ...data.helpCards!,
                heading: esOverride.helpCards.heading,
                intro: esOverride.helpCards.intro,
                cards: data.helpCards!.cards.map((c, i) => esOverride.helpCards.cards[i] ? { ...c, title: esOverride.helpCards.cards[i].title, text: esOverride.helpCards.cards[i].text } : c),
              } : data.helpCards,
              partnerTitle: esOverride.partnerTitle,
              partnerIntro: esOverride.partnerIntro,
              partnerBullets: esOverride.partnerBullets,
            } : data}
            onCtaClick={() => setFormOpen(true)}
            hidePartnerSection
          />
        )}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={esOverride ? { intro: esOverride.partnerIntro, bullets: esOverride.partnerBullets } : data ? { intro: data.partnerIntro, bullets: data.partnerBullets } : undefined}
        />

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Safety reporting requirements", es: "Requisitos de farmacovigilancia", "zh-cn": "安全报告警戒要求" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">
                {lang === "es" ? "Preguntas frecuentes" : lang === "zh-cn" ? "常见问题" : "Frequently Asked Questions"}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{parentService.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={parentService.formHeading}
              subtitle={parentService.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={parentService.formHeading}
          description={parentService.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default PVOffering;
